import React, { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  applyNodeChanges,
  applyEdgeChanges,
  Connection,
  Edge,
  Node,
  ReactFlowInstance,
  NodeChange,
  EdgeChange
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useTranslation } from 'react-i18next';
import useBgPages from '../../../hooks/use-bg-pages.hook';
import Header from '../../../components/layout/header.component';
import Asaid from '../../../components/layout/asaid.component';
import Icon from '../../../components/forms/icon.component';
import { Link, useLocation } from 'react-router-dom';
import NodeType from '../../../components/nodes/nodeType.component';
import { useDarkMode } from '../../../hooks/use-dark-mode.hook';
import SidePanel from './components/side-panel';
import { NodeData, Nodetype } from './types';
import { BotsService } from '../../../services/bots.service';
import { AlertService } from '../../../services/alert.service';
import PreviewPanel from './components/preview-panel';
import MenuBar from './components/menu-bar';
import Loading from '../../../components/loader/loader.component';
import { useFlowData } from '../../../hooks/use-flow-data.hook';
import { FlowsService } from '../../../services/flows.service';

interface DockButton {
  type: Nodetype;
  label: string;
  icon: string;
}
const envOptions = [
  { label: 'Pruebas', value: 1 },
  { label: 'Producción', value: 0 }
];
function FlowBuilder() {
  useBgPages();
  const location = useLocation();
  const initialState = location.state;
  const { _id: botId } = initialState;
  const [selectedEnv, setSelectedEnv] = useState(envOptions[0]);
  const { nodes, edges, setNodes, setEdges, isLoading } = useFlowData(botId, selectedEnv.value);
  const { t } = useTranslation();
  const isDarkTheme = useDarkMode();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
  const [selectedNode, setSelectedNode] = useState<Node<NodeData> | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const dockButtons = useMemo<DockButton[]>(
    () => [
      { type: 'DEFAULT', label: 'Predeterminado', icon: 'ki-note' },
      { type: 'INTERACTIVE', label: 'Interactivo', icon: 'ki-technology-4' },
      { type: 'HTTP', label: 'Validación HTTP', icon: 'ki-router' },
      // { type: 'REDIRECT_AGENT', label: 'Redirigir agente', icon: 'ki-user-square' },
      { type: 'CLOSE', label: 'Finalizar flujo', icon: 'ki-entrance-left' }
    ],
    []
  );

  const nodeTypes = useMemo(
    () => ({
      DEFAULT: NodeType,
      INTERACTIVE: NodeType,
      HTTP: NodeType,
      REDIRECT_AGENT: NodeType,
      CLOSE: NodeType
    }),
    []
  );

  useEffect(() => {
    if (!selectedEdge) return;
    const freshEdge = edges.find((e) => e.id === selectedEdge.id);
    setSelectedEdge(freshEdge || null);
  }, [edges, selectedEdge]);

  const onSelectionChange = useCallback(
    ({ nodes, edges }: { nodes: Node<NodeData>[]; edges: Edge[] }) => {
      if (edges.length > 0) {
        setSelectedEdge(edges[0]);
        setSelectedNode(null);
      } else if (nodes.length > 0) {
        setSelectedNode(nodes[0]);
        setSelectedEdge(null);
      } else {
        setSelectedNode(null);
        setSelectedEdge(null);
      }
    },
    []
  );
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
  }, []);

  const handleSaveNode = useCallback(
    async (nodeId: string, data: NodeData, position: any) => {
      // API
      const node = nodes.find((n) => n.id === nodeId);
      let payload: any = {
        bot: botId,
        name: data.title,
        type: data.type,
        position: {
          x: position.x,
          y: position.y
        },
        ...(data.default && { default: data.default }),
        ...(data.http && { http: data.http }),
        ...(data.close && { close: data.close }),
        ...(data.interactive && { interactive: data.interactive }),
        parent_nodes: node?.data.parent_nodes || [],
        keywords: []
      };
      let saved: any;
      try {
        if (data.meta?.isNew) {
          saved = await BotsService.saveNode(payload);
        } else {
          payload = { ...payload, _id: nodeId };
          saved = await BotsService.saveNode(payload);
        }

        setNodes((nds) =>
          nds.map((n) => {
            if (n.id !== nodeId) return n;
            const newId = saved._id;
            if (data.meta?.isNew) {
              setEdges((eds) =>
                eds.map((e) => ({
                  ...e,
                  source: e.source === nodeId ? newId : e.source,
                  target: e.target === nodeId ? newId : e.target
                }))
              );
              n.id = newId;
            }

            return {
              ...n,
              id: newId,
              data: {
                ...n.data,
                ...data,
                _id: newId,
                meta: { isNew: false }
              }
            };
          })
        );
        AlertService.toastSuccess('Se ha guardado el nodo');
      } catch (err: any) {
        console.log('Error: ', err);
        AlertService.toastError(err.error || err);
      }
    },
    [setNodes]
  );

  const onEdgeClick = useCallback((event: any, edge: Edge) => {
    event.preventDefault();
    setSelectedEdge(edge);
    setSelectedNode(null);
  }, []);

  const onConnect = useCallback(
    async (connection: Connection) => {
      const { source, target, sourceHandle } = connection;
      const srcNode = nodes.find((n) => n.id === source);
      const tgtNode = nodes.find((n) => n.id === target);

      // Si el nodo origen no está configurado
      if (srcNode?.data.meta?.isNew) {
        AlertService.toastError('Primero configura el nodo antes de conectar');
        return;
      }
      // Si el nodo destino no está configurado
      if (tgtNode?.data.meta?.isNew) {
        AlertService.toastError('El nodo destino aún no está configurado');
        return;
      }

      const type = srcNode?.data.type as Nodetype;
      const hasOutgoing = edges.some((e) => e.source === source);

      if (!['HTTP', 'INTERACTIVE'].includes(type)) {
        if (hasOutgoing) {
          AlertService.toastError('Este nodo ya tiene un hijo');
          return;
        }
      } else {
        const existsSameHandle = edges.some(
          (e) => e.source === source && e.sourceHandle === sourceHandle
        );
        if (existsSameHandle) {
          AlertService.toastError('Ya existe una conexión para esa respuesta');
          return;
        }
      }

      if (!sourceHandle) {
        setEdges((eds) =>
          addEdge(
            {
              ...connection,
              type: 'smoothstep'
            },
            eds
          )
        );
      } else {
        setEdges((eds) =>
          addEdge(
            {
              ...connection,
              type: 'smoothstep',
              label: sourceHandle,
              data: { keywords: [sourceHandle] }
            },
            eds
          )
        );
      }

      const keywords =
        srcNode && ['HTTP', 'INTERACTIVE'].includes(srcNode.data.type)
          ? [connection.sourceHandle]
          : [];
      try {
        await BotsService.updateEdges({
          action: 'ADD',
          source,
          target,
          keywords
        });
        AlertService.toastSuccess('Conexión creada');
      } catch (err: any) {
        AlertService.toastError(err.error || err);
      }
    },
    [nodes, edges, setEdges]
  );

  const handleDeleteEdge = useCallback(
    async (edgeId: string) => {
      const edge = edges.find((e) => e.id === edgeId);
      if (!edge) return;

      const { source, target, sourceHandle } = edge;
      const srcNode = nodes.find((n) => n.id === source);
      const keywords =
        srcNode && ['HTTP', 'INTERACTIVE'].includes(srcNode.data.type) ? [sourceHandle] : [];

      AlertService.warning(
        `¡ Atención !, estás a punto de eliminar la conexión`,
        `¿Estás seguro?`,
        async () => {
          try {
            await BotsService.updateEdges({
              action: 'DELETE',
              source,
              target,
              keywords
            });
            setEdges((eds) => eds.filter((e) => e.id !== edgeId));
            setSelectedEdge(null);
            AlertService.toastSuccess('Se ha eliminado la conexión');
          } catch (err: any) {
            console.log('Error: ', err);
            AlertService.toastError(err.error || err);
          }
        },
        'Eliminar'
      );
    },
    [edges, nodes, setEdges]
  );

  const onAddEdgeKeyword = useCallback(
    async (edgeId: string, newKeyword: string) => {
      const edge = edges.find((e) => e.id === edgeId);
      if (!edge) return;

      const { source, target } = edge;
      const prevKeywords: string[] = (edge.data as any).keywords || [];
      const updatedKeywords = [...prevKeywords, newKeyword];
      try {
        await BotsService.updateEdges({
          action: 'ADD',
          source,
          target,
          keywords: updatedKeywords
        });

        setEdges((eds) =>
          eds.map((e) => {
            if (e.id !== edgeId) return e;
            return {
              ...e,
              label: updatedKeywords.join(', '),
              data: { ...(e.data as any), keywords: updatedKeywords }
            };
          })
        );

        AlertService.toastSuccess('Keyword agregado');
      } catch (err: any) {
        AlertService.toastError(err.error || err);
      }
    },
    [edges, setEdges]
  );

  const onRemoveEdgeKeyword = useCallback(
    async (edgeId: string, keywordToRemove: string) => {
      const edge = edges.find((e) => e.id === edgeId);
      if (!edge) return;

      const { source, target } = edge;
      const prevKeywords: string[] = (edge.data as any).keywords || [];
      const nextKeywords = prevKeywords.filter((k) => k !== keywordToRemove);
      try {
        await BotsService.updateEdges({
          action: 'ADD',
          source,
          target,
          keywords: nextKeywords
        });

        setEdges((eds) =>
          eds.map((e) => {
            if (e.id !== edgeId) return e;
            return {
              ...e,
              label: nextKeywords.join(', '),
              data: { ...(e.data as any), keywords: nextKeywords }
            };
          })
        );

        AlertService.toastSuccess('Keyword eliminado');
      } catch (err: any) {
        AlertService.toastError(err.error || err);
      }
    },
    [edges, setEdges]
  );

  const onDragOver = useCallback((event: any) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const type = e.dataTransfer.getData('application/reactflow') as Nodetype;
      if (!type || !reactFlowInstance) return;
      const position = reactFlowInstance.screenToFlowPosition({
        x: e.clientX - 220,
        y: e.clientY - 50
      });
      const id = `${Date.now()}`;
      const blank = getBlankNodeData(type);
      setNodes((nds) => {
        const isFirst = nds.length === 0;
        const newNode: Node<NodeData> = {
          id,
          type,
          position,
          data: {
            ...blank,
            isMain: isFirst
          }
        };
        return [...nds, newNode];
      });
    },
    [reactFlowInstance, setNodes]
  );

  const DockButtonComponent = useCallback(
    ({ btn, isLast }: { btn: DockButton; isLast: boolean }) => (
      <>
        <div
          className="rounded-12 p-3 hover-scale cursor-grab"
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData('application/reactflow', btn.type);
            e.dataTransfer.effectAllowed = 'move';
          }}
        >
          <div className="d-flex align-items-center">
            <Icon icon={btn.icon} size="fs-2x" className="hover-icon" />
            <span className="fw-bolder ms-2 text-truncate">{btn.label}</span>
          </div>
        </div>
        {!isLast && <span className="text-info fw-bolder mx-2">|</span>}
      </>
    ),
    []
  );

  const getBlankNodeData = (type: Nodetype): NodeData => {
    switch (type) {
      case 'DEFAULT':
        return {
          type,
          title: 'Predeterminado',
          isMain: false,
          meta: {
            isNew: true
          },
          default: {
            type: 'TEXT',
            text: {
              es: ''
            }
          }
        };
      case 'INTERACTIVE':
        return {
          type,
          title: 'Interactivo',
          meta: {
            isNew: true
          },
          isMain: false,
          interactive: {
            type: '',
            validators: [],
            attempts_before_close: {
              attempts: 2,
              type: 'CLOSE',
              message: { es: '' },
              close: { category: '' }
            },
            triggers: []
          }
        };
      case 'HTTP':
        return {
          type,
          title: 'Validación HTTP',
          meta: {
            isNew: true
          },
          isMain: false,
          http: {
            url_dev: '',
            url_prod: '',
            responses: []
          }
        };
      case 'REDIRECT_AGENT':
        return {
          type,
          title: 'Redirigir agente',
          isMain: false,
          meta: {
            isNew: true
          },
          redirect_agent: {
            message: {
              es: 'Serás redirigido a un agente'
            },
            agents: ['Agente 1', 'Agente 2', 'Agente 3']
          }
        };
      case 'CLOSE':
        return {
          type,
          title: 'Finalizar flujo',
          meta: {
            isNew: true
          },
          isMain: false,
          close: {
            message: {
              es: ''
            },
            category: ''
          }
        };
    }
  };

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setNodes((nds) => applyNodeChanges(changes, nds));
    },
    [setNodes]
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      setEdges((eds) => applyEdgeChanges(changes, eds));
    },
    [setEdges]
  );

  const EmptyBadge = () => (
    <div className="badge badge-info bg-light-info text-info empty-flows mt-3 me-3 fs-7">
      <Icon icon="ki-information-2 " size="fs-2x" className="me-2" type="info" />
      Arrastra un nodo para empezar a interactuar
    </div>
  );

  const onNodeDragStop = useCallback(async (event: any, node: Node) => {
    const nodeId = node.id;
    if (!nodeId) return;
    if (node.data.meta?.isNew) return;
    try {
      await BotsService.updateNodeCoords({
        _id: node.id,
        x: node.position.x,
        y: node.position.y
      });
    } catch (err: any) {
      console.log('Error: ', err);
      AlertService.toastError(err.error || err);
    }
  }, []);

  const handlePublish = async () => {
    const isConfirmed = await AlertService.warning(
      t('general.atention'),
      '¿Estás seguro que deseas publicar el flujo?',
      () => Promise.resolve(true)
    );
    if (!isConfirmed) return;
    try {
      await FlowsService.deployFlow({ bot: botId });
      AlertService.toastSuccess('Se ha publicado el flujo');
    } catch (err: any) {
      console.log('Error: ', err);
      AlertService.toastError(err.error || err);
    }
  };

  return (
    <div className="page d-flex flex-row flex-column-fluid">
      <Asaid />

      <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
        <Header />
        <div
          className="row bg-light-info rounded-12 d-flex flex-column flex-column-fluid m-0 pb-0 pt-1"
          id="kt_content"
        >
          <div className="d-flex justify-content-between align-items-center my-2 px-5">
            <div className="">
              <span className="d-flex align-items-center gap-2">
                <span className="fw-bold fs-2">
                  Configura tu <span className="text-info">flujo</span>:{' '}
                </span>
                <span className="badge bg-light-primary text-primary fs-3">
                  {initialState?.name || 'Crear nuevo flujo'}
                </span>
              </span>
            </div>
            <Link to="/sendCampaigns/flows-answers" className="btn ">
              <Icon icon="ki-arrow-left" size="fs-2" />
              {t('general.return')}
            </Link>
          </div>

          {/* Contenedor del React Flow */}
          <div
            className="reactflow-wrapper position-relative border border-dashed border-gray-300"
            ref={reactFlowWrapper}
            style={{
              height: 'calc(100vh - 11.8rem)',
              background: isDarkTheme ? '#1B1C1E' : '#ffff'
            }}
          >
            {isLoading && <Loading />}
            {!isLoading && (
              <>
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onConnect={onConnect}
                  onInit={setReactFlowInstance}
                  onDrop={onDrop}
                  onDragOver={onDragOver}
                  onNodeDragStop={onNodeDragStop}
                  nodeTypes={nodeTypes}
                  onSelectionChange={onSelectionChange}
                  onPaneClick={onPaneClick}
                  onEdgeClick={onEdgeClick}
                  defaultViewport={{ x: 290, y: 190, zoom: 1.1 }}
                  fitView
                >
                  <Controls />
                  <Background color="#aaa" gap={16} />
                </ReactFlow>

                {/* Si no hay nodos */}
                {nodes.length === 0 && <EmptyBadge />}

                {/* Panel lateral */}
                <SidePanel
                  selectedNode={selectedNode}
                  selectedEdge={selectedEdge}
                  onSave={handleSaveNode}
                  onDeleteEdge={handleDeleteEdge}
                  onAddEdgeKeyword={onAddEdgeKeyword}
                  onRemoveEdgeKeyword={onRemoveEdgeKeyword}
                />

                {/* Panel Preview */}
                {previewOpen && (
                  <PreviewPanel botId={botId} onClose={() => setPreviewOpen(false)} />
                )}

                {/* Menu Superior */}
                {nodes.length > 0 && (
                  <MenuBar
                    options={envOptions}
                    selected={selectedEnv}
                    onEnvChange={setSelectedEnv}
                    onOpenPreview={() => setPreviewOpen(true)}
                    onPublish={() => handlePublish()}
                  />
                )}

                {/* Dock inferior */}
                <div className="node-dock d-flex align-items-center justify-content-center px-4 py-1 bg-light-info">
                  {dockButtons.map((btn, index) => (
                    <DockButtonComponent
                      key={btn.type}
                      btn={btn}
                      isLast={index === dockButtons.length - 1}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FlowBuilder;
